import React, { useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { compose } from 'redux';
import LanguageSelector from 'nallian-shared-ui/lib/layout/LanguageSelector';
import communityService from 'nallian-shared-ui/lib/services/communityService';
import { msalConfig } from 'nallian-shared-ui/lib/authentication/constants/config';
import { Loader, Flex, Divider, Button } from 'nallian-shared-ui/lib/components';
import { Text } from 'nallian-shared-ui/lib/layout/styles';
import InvitationError from '../InvitationError';
import { PRINCIPAL_STATES, INVITATION_STATES, ORGANISATION_STATES } from '../../../app/constants';
import invitationService from '../../services/invitationService';
import { List, ListItem } from '../../styles';

const InviteOverview = ({ dispatchInvitationGet, invitationFetch, myCommunityFetch }) => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => dispatchInvitationGet(token), [dispatchInvitationGet, token]);

  const handleRedirect = useCallback(() => {
    const principalState = get(invitationFetch.value, 'principal.activeState');

    // -------------------------------------------------------------------------
    // WARNING!!!
    // If the url to /overview ever changes, the backend needs to be prepared
    // first! See bug #50485
    if (principalState === PRINCIPAL_STATES.ACTIVE) {
      navigate(`/overview`);
    } else {
      window.location.href = `${msalConfig.auth.authority}/Account/Invitation/${token}?returnUrl=${window.location.origin}/overview`;
    }
    // -------------------------------------------------------------------------
  }, [invitationFetch.value, token, navigate]);

  if (invitationFetch.pending || myCommunityFetch.pending) {
    return <Loader inline active />;
  }

  if (invitationFetch.rejected) {
    return <InvitationError />;
  }

  if (invitationFetch.value?.invitationState === INVITATION_STATES.EXPIRED) {
    return <InvitationError expired />;
  }

  if (invitationFetch.value?.invitationState === INVITATION_STATES.RETRACTED) {
    return <InvitationError retracted />;
  }

  if (invitationFetch.value?.invitationState === INVITATION_STATES.CONSUMED) {
    const organisationActive =
      invitationFetch.value?.organisation?.activeStateId === ORGANISATION_STATES.ACTIVE;

    return (
      <>
        <LanguageSelector isPublic isFixed isOnWhiteBackground />
        <Flex direction="column">
          <Text>{t('inviteAlreadyUsed')}</Text>
          <Text>
            {organisationActive
              ? t('invitationUsedProceedLogin')
              : t('invitationUsedProceedProcess')}
          </Text>
          <Flex fluid justifyContent="center">
            <Button
              content={organisationActive ? t('signIn') : t('completeRegistration')}
              onClick={handleRedirect}
              btnprimary
            />
          </Flex>
        </Flex>
      </>
    );
  }

  return (
    <>
      <LanguageSelector isPublic isFixed isOnWhiteBackground />
      <Flex direction="column">
        <Text>{t('youHaveBeenInvited')}</Text>
        <Text align="row">
          <Trans
            i18nKey="invitationInfo"
            values={{
              username: get(invitationFetch.value, 'inviter.fullName', '-'),
              organisation: get(invitationFetch.value, 'inviter.organisation.name', '-'),
              community: get(myCommunityFetch.value, 'name', 'Nallian'),
            }}
            components={{ bold: <strong /> }}
          />
        </Text>
        <Text>{t('confirmInvitationInfo')}</Text>
        <Divider />
        <Flex fluid justifyContent="center">
          <List>
            <ListItem>
              <strong>{t('name')}:</strong>
              {get(invitationFetch.value, 'principal.fullName', '-')}
            </ListItem>
            <ListItem>
              <strong>{t('organisation')}:</strong>
              {get(invitationFetch.value, 'organisation.name', '-')}
            </ListItem>
            <ListItem>
              <strong>{t('emailAddress')}:</strong>
              {get(invitationFetch.value, 'principal.primaryEmailAddress', '-')}
            </ListItem>
          </List>
        </Flex>
        <Divider />
        <Flex fluid justifyContent="center">
          <Button content={t('proceed')} onClick={handleRedirect} btnprimary />
        </Flex>
      </Flex>
    </>
  );
};

InviteOverview.propTypes = {
  dispatchInvitationGet: PropTypes.func.isRequired,
  invitationFetch: PropTypes.object.isRequired,
  myCommunityFetch: PropTypes.object.isRequired,
};

export default compose(communityService, invitationService)(InviteOverview);
